import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface StepWelcomeProps {
  email: string;
  onEmailChange: (email: string) => void;
  onContinue: () => void;
  onGoogleSignIn: () => Promise<void>;
  error?: string | null;
}

export const StepWelcome: React.FC<StepWelcomeProps> = ({
  email,
  onEmailChange,
  onContinue,
  onGoogleSignIn,
}) => {
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleClick = async () => {
    setGoogleLoading(true);
    try {
      await onGoogleSignIn();
      // Page will redirect to Google - loading stays on until navigation
    } catch {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      onContinue();
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center">
      {/* Hero background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(/hero/pexels-the-trvlr-3793462.jpg)' }}
      >
        <div className="absolute inset-0 bg-black/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md mx-auto px-6">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logos/logo-wide.png"
            alt="DARAK"
            className="h-12 mx-auto mb-6 brightness-0 invert"
          />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Join DARAK
          </h1>
          <p className="text-white/80 text-lg">
            Discover unique stays across Lebanon
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Google Sign-In */}
          <button
            type="button"
            onClick={handleGoogleClick}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
          >
            {googleLoading ? (
              <LoadingSpinner />
            ) : (
              <GoogleIcon />
            )}
            {googleLoading ? 'Signing in...' : 'Continue with Google'}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">or continue with email</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Email form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                required
                placeholder="your@email.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={!email.trim()}
              className="w-full py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
              Continue
            </button>
          </form>

          {/* Social proof */}
          <p className="mt-6 text-center text-sm text-gray-400">
            Join 1,000+ travelers exploring Lebanon
          </p>
        </div>

        {/* Login link */}
        <p className="mt-6 text-center text-white/80 text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-white font-medium hover:underline">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
};

const GoogleIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const LoadingSpinner: React.FC = () => (
  <svg className="w-5 h-5 animate-spin text-gray-500" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

export default StepWelcome;
